import { useKeycloak } from "@react-keycloak/web";
import { FunctionComponent, useEffect } from "react";
import { connect } from "react-redux";
import { useRoutes } from "react-router-dom";
import { codeHttpClient } from "core/http/clients/code/code.http";
import { authAsync } from "core/store/modules/auth";
import { Dispatch, RootState } from "core/store/store.types";
import routes from "routes";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import useTheme from "core/hooks/useTheme";
import createTheme from "core/theme";
import { Layout } from "containers/layout";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import it from "date-fns/locale/it";
import en from "date-fns/locale/en-GB";
import { useTranslation } from "react-i18next";
import { Locale } from "date-fns";
import { LoginPage } from "pages/login";
import { AxiosError, AxiosResponse } from "axios";
import { productAsync } from "core/store/modules/product";
import { CookieBanner } from "components/atoms/cookie-banner";
import { ActAsProvider } from "contexts/ActAsContext";
import { ACT_AS_HEADER, getActAsSessionId } from "core/act-as/act-as.session";
import { isActAsAllowedRequest } from "core/act-as/act-as.allowlist";
import { syncActAsSession } from "core/act-as/act-as.sync";

type languageOptionsType = {
  [key: string]: {
    locale: Locale;
  };
};

interface OwnProps {}
interface StateProps {}
interface DispatchProps {
  getUser: () => void;
  logout: () => void;
  getProductList: () => void;
  getPlanList: () => void;
}
export type Props = OwnProps & StateProps & DispatchProps;

const App: FunctionComponent<Props> = ({
  getUser,
  logout,
  getProductList,
  getPlanList,
}) => {
  const { keycloak } = useKeycloak();
  const { i18n } = useTranslation();
  const { theme } = useTheme();
  const content = useRoutes(routes);
  const languageOptions: languageOptionsType = {
    it: { locale: it },
    en: { locale: en },
  };
  const selectedLanguage = languageOptions[i18n.language] ?? {
    it: { locale: it },
  };

  useEffect(() => {
    if (!keycloak.authenticated) return;

    const requestInterceptor = codeHttpClient.interceptors.request.use(
      (config) => {
        config.headers.Authorization = `Bearer ${keycloak.token}`;
        // Delega "lavora per conto di": l'id di sessione si legge dal modulo, MAI da
        // uno stato React, perche' questo callback resta vivo con i valori catturati
        // al momento della registrazione. L'header parte solo sulle coppie
        // rotta+metodo che il backend accetta di delegare: altrove sarebbe un 403 e
        // romperebbe il guscio dell'app (a partire da GET /account).
        const actAsSessionId = getActAsSessionId();
        if (actAsSessionId && isActAsAllowedRequest(config.url, config.method)) {
          config.headers[ACT_AS_HEADER] = actAsSessionId;
        } else {
          delete config.headers[ACT_AS_HEADER];
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    const responseInterceptor = codeHttpClient.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        console.error(
          "ERROR: " + error.response?.status + " on " + error.config?.url
        );
        if (error.response && error.response.status === 401) {
          console.log("Auth FAILED");
          handleLogout();
        }
        if (
          error.response &&
          error.response.status === 402 &&
          (error.response.data as any)?.code === "account_suspended"
        ) {
          // Il backend ha bloccato un'operazione perche' l'account e' sospeso per
          // mancato pagamento: ricarico /account cosi' billingStatus si aggiorna e
          // compare il banner bloccante.
          getUser();
        }
        if (
          error.response &&
          error.response.status === 403 &&
          error.config?.headers?.[ACT_AS_HEADER] &&
          // La rotta di gestione della delega non partecipa: altrimenti un suo 403
          // farebbe ripartire il controllo all'infinito.
          !error.config?.url?.includes("/legals/act-as")
        ) {
          // La richiesta portava l'header e il backend l'ha rifiutata (delega scaduta,
          // chiusa da un'altra scheda, flag spento, endpoint fuori allowlist). Chiedo al
          // server se la sessione esiste ancora invece di indovinarlo dal messaggio:
          // se non esiste, il banner sparisce e l'header smette di partire.
          syncActAsSession();
        }
        // Propagate the error if not a 401
        throw error;
      }
    );

    // Senza eject, ogni refresh del token impilava una coppia di interceptor in piu':
    // le richieste venivano riscritte N volte e il valore vincente era quello della
    // closure piu' vecchia.
    return () => {
      codeHttpClient.interceptors.request.eject(requestInterceptor);
      codeHttpClient.interceptors.response.eject(responseInterceptor);
    };
  }, [keycloak.authenticated, keycloak.token]);

  useEffect(() => {
    if (keycloak.authenticated) {
      // console.log(keycloak.token)
      getUser();
      getProductList();
      getPlanList();
      // Deve stare QUI e non dentro ActAsProvider: gli effect dei figli girano prima di
      // quelli del padre, quindi da li' la chiamata partirebbe senza l'Authorization
      // dell'interceptor (401 -> logout). Questo effect e' dichiarato dopo quello che
      // registra gli interceptor, quindi l'ordine e' garantito.
      syncActAsSession();
    }
  }, [keycloak.authenticated, getUser]);

  const handleLogout = () => {
    logout();
    keycloak.logout();
  };

  if (keycloak.authenticated) {
    return (
      <MuiThemeProvider theme={createTheme(theme)}>
        {/* @ts-ignore */}
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={selectedLanguage.locale}
        >
          <ActAsProvider>
            <Layout handleLogout={handleLogout}>{content}</Layout>
          </ActAsProvider>
          <CookieBanner />
        </LocalizationProvider>
      </MuiThemeProvider>
    );
  } else {
    return (
      <MuiThemeProvider theme={createTheme(theme)}>
        <LoginPage />
      </MuiThemeProvider>
    );
  }
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state: RootState) => ({}),
  (dispatch: Dispatch) => ({
    getUser: () => dispatch(authAsync.getUser()),
    logout: () => dispatch(authAsync.logout()),
    getProductList: () => dispatch(productAsync.getProductList()),
    getPlanList: () => dispatch(productAsync.getPlanList()),
  })
)(App);
